import type { Component } from "solid-js";
import { Show, createEffect } from "solid-js";
import ApiService from "../../services/ApiService";
import { useNavigate } from "@solidjs/router";
import type FolderId from "../../state/FolderId";
import { isEmpty, isNil } from "lodash-es";
import { useLocalUserSettings } from "../../state/localUserSettings";
import PermissionService from "../../services/PermissionService";
import { useCustomerState } from "../../state/customer";
import EmptyState from "../../components/emptyState/EmptyState";
import { useTranslation } from "../../state/i18n";
import { useModalManager } from "../../components/modal/ModalManager";
import CreateFolderModal from "./CreateFolderModal";

const OverviewPage: Component = () => {
  const navigate = useNavigate();
  const { lastFolderId } = useLocalUserSettings();
  const { customerId } = useCustomerState();
  const { open } = useModalManager();
  const { t } = useTranslation();
  const foldersQuery = ApiService.getFolders();
  const canCreateFolder = PermissionService.canCreateFolder(customerId);
  const navigateToFolder = (folderId: FolderId) => {
    navigate(`folders/${folderId}`);
  };

  createEffect(() => {
    if (foldersQuery.status !== "success" || foldersQuery.isFetching) {
      return;
    }
    const folders = foldersQuery.data;
    if (isEmpty(folders)) {
      return;
    }

    const lastFolderIdForCustomer = lastFolderId?.[customerId.toString()];

    if (
      !isNil(lastFolderIdForCustomer) &&
      folders.some((folder) => folder.id.equals(lastFolderIdForCustomer)) &&
      PermissionService.canEditFolder(customerId, lastFolderIdForCustomer)
    ) {
      navigateToFolder(lastFolderIdForCustomer);
    } else {
      navigateToFolder(folders[0].id);
    }
  });

  return (
    <>
      <Show when={isEmpty(foldersQuery.data) && canCreateFolder && !foldersQuery.isLoading}>
        <EmptyState
          title={t(($) => $.FOLDER_PAGE_EMPTY_STATE_TITLE)}
          description={t(($) => $.FOLDER_PAGE_EMPTY_STATE_DESCRIPTION)}
          illustration={{
            primaryIcon: "folder",
            secondaryIcon: "search",
          }}
          action={{
            variant: "light",
            icon: "plus",
            text: t(($) => $.FOLDER_PAGE_EMPTY_STATE_ACTION),
            onClick: () =>
              open({
                content: () => <CreateFolderModal customerId={customerId} />,
              }),
          }}
        />
      </Show>
      <Show when={isEmpty(foldersQuery.data) && !canCreateFolder && !foldersQuery.isLoading}>
        <EmptyState
          title={t(($) => $.FOLDER_PAGE_EMPTY_STATE_NO_PERMISSION_TITLE)}
          description={t(($) => $.FOLDER_PAGE_EMPTY_STATE_NO_PERMISSION_DESCRIPTION)}
          illustration={{
            primaryIcon: "lockClosed",
          }}
        />
      </Show>
    </>
  );
};

export default OverviewPage;
